/**
 * What the Publish workspace and the Export dialog compute from the open project (W2-09 publish-import): export
 * options, blocking problems, the lowered IR the export writes (cached per committed document state and options, so
 * the two Publish panels and the dialog share one lowering), the conversion report and compatibility.
 */

import { useCallback, useMemo } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { LowerReport } from '../../core/ir/types';
import type { AssetId } from '../../core/model/types';
import type { MessageRef } from '../../core/util/types';
import type { ExportOptions } from '../../formats/types';
import type { DocStore } from '../../state/types';
import { currentOptions, lowerForExport, overrideWarning, type Lowered } from './exportFlow';
import { useDeferred, useDocVersion, usePrefs, useProblems, useSession, type ProblemsView } from './hooks';
import {
  approximateEffects,
  bezierCollisions,
  blockingIssues,
  compatibility,
  conversionNotes,
  localBlocking,
  songLengthSec,
  type BezierCollisions,
  type CompatReport,
  type PreflightIssue,
} from './preflight';
import { builtinShaderFidelity } from '../../render/preview/effects/builtin/index';
import type { ExportPrefs } from './prefs';
import { exportedAssetIds, useStoredMissing } from './storedFiles';

export const EMPTY_LOWER_REPORT: LowerReport = {
  bakedSpeed: [],
  paddedLines: [],
  staleRawTriples: 0,
  gapsMaterialised: 0,
};

/** The lowering of an export plus what is derived from it. */
export interface Conversion {
  readonly lowered: Lowered;
  readonly collisions: BezierCollisions;
  readonly overrideWarning: MessageRef | null;
  readonly notes: readonly MessageRef[];
}

const cache = new WeakMap<DocStore, { readonly key: string; readonly value: Conversion }>();

/**
 * Conversion of the store's committed state for these options (cached per store, sequence number and options).
 * `seq` is the store's sequence number the caller rendered with (it keys the cache).
 */
export function conversionFor(
  store: DocStore,
  engine: AppServices['engine'],
  opts: ExportOptions,
  seq: number = store.seq,
): Conversion {
  const key = `${String(seq)}|${JSON.stringify(opts)}`;
  const hit = cache.get(store);
  if (hit !== undefined && hit.key === key) return hit.value;
  const lowered = lowerForExport(store.doc, store.idx, opts, engine);
  const collisions = bezierCollisions(lowered.chart);
  const warning = overrideWarning(store.doc, opts);
  const value: Conversion = {
    lowered,
    collisions,
    overrideWarning: warning,
    notes: conversionNotes(lowered.report, collisions, warning),
  };
  cache.set(store, { key, value });
  return value;
}

export interface PublishModel {
  readonly store: DocStore;
  /** Moves with every committed document change. */
  readonly version: number;
  readonly prefs: ExportPrefs;
  readonly options: ExportOptions;
  readonly songSec: number | null;
  readonly problems: ProblemsView;
  readonly blocking: readonly PreflightIssue[];
  /** Files the export writes whose bytes the browser lost (UX-066). */
  readonly storedMissing: ReadonlySet<AssetId>;
  /** Null while it is being computed (after the first paint). */
  readonly conversion: Conversion | null;
  readonly compat: CompatReport;
}

/** Everything the Publish panels and the Export dialog show for the open project, live. */
export function usePublishModel(
  services: AppServices,
  store: DocStore,
  /**
   * 'workspace': blocking problems of the Phira package (the Publish summary); 'dialog': of the chosen target (a
   * chart file alone has no song, files or song-select preview to check).
   */
  scope: 'workspace' | 'dialog' = 'workspace',
): PublishModel {
  const version = useDocVersion(store);
  const basePrefs = usePrefs(services);
  useSession();
  const problems = useProblems(services);
  const prefs = basePrefs;
  const songSec = songLengthSec(store.doc, services.audio.music);
  // Stable options object: the same JSON gives the same object (the deferred lowering restarts only on changes).
  const optionsJson = JSON.stringify(currentOptions(store.doc, store.projectId, prefs));
  const options = useMemo(() => JSON.parse(optionsJson) as ExportOptions, [optionsJson]);
  const chartOnly = scope === 'dialog' && options.target === 'chartJson';
  const doc = store.doc;
  const idx = store.idx;
  const storedIds = useMemo(() => exportedAssetIds(doc, idx), [doc, idx]);
  const storedMissing = useStoredMissing(services, storedIds);
  // A chart file alone carries no song, files or song-select preview: neither the local checks nor the checker's
  // package-only rules of those block it.
  const blocking = blockingIssues(
    localBlocking(store.doc, store.idx, songSec, chartOnly, storedMissing),
    problems.problems,
    chartOnly,
  );
  const engine = services.engine;
  const seq = store.seq;
  const compute = useCallback(() => conversionFor(store, engine, options, seq), [store, engine, options, seq]);
  const conversion = useDeferred(compute);
  const compat = compatibility({
    doc: store.doc,
    idx: store.idx,
    blocking: blocking.length,
    overrideWarning: conversion?.overrideWarning ?? null,
    report: conversion?.lowered.report ?? EMPTY_LOWER_REPORT,
    collisions: conversion?.collisions ?? { curves: 0, events: 0 },
    infoTxt: options.profile === 'rpe-compatible' && options.includeInfoTxt,
    approxEffects: approximateEffects(store.doc, builtinShaderFidelity),
  });
  return { store, version, prefs, options, songSec, problems, blocking, storedMissing, conversion, compat };
}
